import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { IonInputOtp, IonIcon } from "@ionic/react";
import { chevronBackOutline, logoApple, logoGoogle } from "ionicons/icons";
import { toast } from "@/utils/toast";
import { userHeaders } from "@/utils/userAuth";
import { registerPush } from "@/utils/nativePush";
import RippleButton from "@/components/RippleButton";
import { isNative } from "@/utils/nativeHttp";
import MintSlipLogo from "../assests/mintslip-logo.png";
import "../login.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// User sign-in — the whodat flow: no password. "Continue with Apple" and
// "Continue with Google" up top (the backend owns each OAuth flow: it sends the
// browser to the provider, handles the callback, then bounces back here with a
// one-time ticket we trade for the session), and "Use email instead" reveals
// the one-time-code form: enter an email, get a 6-digit code, type it into the
// segmented field; the sixth digit verifies on its own. The account is created
// on the first successful sign-in, so there's no separate signup.
// The referral code a link brought this person in with (App.js keeps
// ?ref=CODE): a fresh account claims it once the backend has a session for
// it, then it is forgotten. The backend only attaches it to a new account.
async function claimReferral() {
  let saved = null;
  try { saved = JSON.parse(localStorage.getItem("mintslipRef") || "null"); } catch { saved = null; }
  if (!saved || !saved.code) return;
  try {
    const res = await fetch(`${BACKEND_URL}/api/user/referrals/attach`, {
      method: "POST", headers: userHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ code: saved.code }),
    });
    if (res.ok) localStorage.removeItem("mintslipRef");
  } catch { /* the next sign-in tries again */ }
}

export default function UserLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  // Google and Apple refuse OAuth inside a WebView, so the native app goes
  // straight to the email code (the account is the same either way).
  const [providers, setProviders] = useState(isNative ? { google: false, apple: false } : { google: true, apple: true });
  const [nativeGoogle, setNativeGoogle] = useState(""); // the client id the OS Google sheet initializes with
  const [googleBusy, setGoogleBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(isNative);
  const [email, setEmail] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [exchanging, setExchanging] = useState(false);

  // ?next=/app/...: where to land after signing in (only app paths). Kept for
  // the tab so a Google round trip back to this page doesn't lose it.
  const NEXT_KEY = "ms:loginNext";
  const landing = () => {
    let next = "";
    try { next = sessionStorage.getItem(NEXT_KEY) || ""; sessionStorage.removeItem(NEXT_KEY); } catch { /* storage off */ }
    return /^\/app(\/|\?|$)/.test(next) ? next : "/app";
  };
  useEffect(() => {
    const next = new URLSearchParams(window.location.search).get("next") || "";
    if (/^\/app(\/|\?|$)/.test(next)) { try { sessionStorage.setItem(NEXT_KEY, next); } catch { /* storage off */ } }
  }, []);

  // Already signed in → straight to the dashboard (or where they were headed)
  useEffect(() => {
    if (localStorage.getItem("userToken")) navigate(landing());
  }, [navigate]); // eslint-disable-line

  const post = async (path, body) => {
    const res = await fetch(`${BACKEND_URL}${path}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
    return data;
  };

  const signedIn = (data) => {
    localStorage.setItem("userToken", data.token);
    localStorage.setItem("userInfo", JSON.stringify(data.user));
    claimReferral(); // an account created through someone's link credits them
    // An admin's email signs in both sides at once: the backend sends the
    // admin session along, stored the same way the admin login page stores it
    if (data.adminSession && data.adminSession.token) {
      localStorage.setItem("adminToken", data.adminSession.token);
      localStorage.setItem("adminInfo", JSON.stringify(data.adminSession.admin || {}));
      localStorage.setItem("adminRole", data.adminSession.role || "admin");
      localStorage.setItem("adminPermissions", JSON.stringify(data.adminSession.permissions || null));
    }
    registerPush(); // native: this device now belongs to the signed-in account
    navigate(landing());
  };

  // The backend bounces failed OAuth sign-ins back here with a reason, and
  // successful ones with a one-time ticket to trade for the session token.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get("error");
    const ticket = params.get("ticket");
    if (!error && !ticket) return;
    navigate("/login", { replace: true });
    if (error) { toast.error(error); return; }
    setExchanging(true);
    post("/api/user/login/oauth/exchange", { ticket })
      .then(signedIn)
      .catch((err) => toast.error(err.message || "Sign-in expired. Please try again."))
      .finally(() => setExchanging(false));
  }, []); // eslint-disable-line

  // Hide a provider the server says isn't configured (both show by default)
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/user/login/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        // The native app uses the OS Google sheet (web OAuth is refused in
        // WebViews), initialized with the backend's own client id
        if (isNative) { setNativeGoogle(d.google !== false ? (d.googleClientId || "") : ""); return; }
        setProviders({ google: d.google !== false, apple: d.apple !== false });
      })
      .catch(() => {});
  }, []);

  // Native Google sign-in (the whodat way): the OS account sheet hands back a
  // Google ID token and the backend opens the same session the web flow does.
  const nativeGoogleSignIn = async () => {
    if (googleBusy || !nativeGoogle) return;
    setGoogleBusy(true);
    try {
      const { SocialLogin } = await import("@capgo/capacitor-social-login");
      await SocialLogin.initialize({ google: { webClientId: nativeGoogle } });
      // no scopes: the default sign-in already carries email+name in the ID
      // token, and the scopes path needs a native MainActivity change
      const res = await SocialLogin.login({ provider: "google", options: {} });
      const idToken = (res && res.result && res.result.idToken) || (res && res.idToken);
      if (!idToken) throw new Error("Google did not confirm the sign-in.");
      signedIn(await post("/api/user/login/google-native", { idToken }));
    } catch (err) {
      if (!/cancel/i.test(String((err && err.message) || ""))) {
        toast.error((err && err.message) || "Google sign-in failed. Please try again.");
      }
    } finally {
      setGoogleBusy(false);
    }
  };

  // The backend owns each OAuth flow: navigate the page to its start URL; it
  // redirects to the provider, handles the callback and lands back on /login.
  // The hop waits a beat so the button's ripple plays out before the page
  // leaves (navigating on release cut it off before it was visible).
  const [leaving, setLeaving] = useState(false);
  const startOAuth = (provider) => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => { window.location.href = `${BACKEND_URL}/api/auth/${provider}/start`; }, 280);
    // If the page is still here well after that (the hop was blocked), let the button work again
    setTimeout(() => setLeaving(false), 5000);
  };
  // Coming back from the provider without finishing (browser Back restores
  // this page from the back-forward cache with its state intact, so `leaving`
  // would still be true and the buttons dead): arm them again.
  useEffect(() => {
    const revive = (e) => { if (e.type !== "pageshow" || e.persisted) setLeaving(false); };
    window.addEventListener("pageshow", revive);
    window.addEventListener("focus", revive);
    return () => { window.removeEventListener("pageshow", revive); window.removeEventListener("focus", revive); };
  }, []);

  const sendCode = async (e) => {
    e.preventDefault();
    const addr = email.trim();
    if (!addr || busy) return;
    setBusy(true);
    try {
      await post("/api/user/login/code/request", { email: addr });
      setCode("");
      setCodeSent(true);
    } catch (err) {
      toast.error(err.message || "Could not send the code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const doVerify = async () => {
    if (code.length !== 6 || verifying) return;
    setVerifying(true);
    try {
      signedIn(await post("/api/user/login/code/verify", { email: email.trim(), code }));
    } catch (err) {
      toast.error(err.message || "That code is incorrect or has expired.");
    } finally {
      setVerifying(false);
    }
  };
  const verifyCode = (e) => { e.preventDefault(); doVerify(); };

  // Back out of the sign-in screen: genuinely go back to wherever the user
  // came from inside the app. The router numbers its entries (history.state.idx,
  // 0 for the first page of the session), so anything above 0 means there is
  // an in-app page behind us; opened cold (fresh tab / link) there is nowhere
  // back, so go home.
  const goBack = () => {
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) navigate(-1);
    else navigate("/", { replace: true });
  };

  // Verify as soon as the 6th digit lands
  useEffect(() => {
    if (code.length === 6) doVerify();
  }, [code]); // eslint-disable-line

  return (
    <div className="ms-login-page">
      <RippleButton type="button" className="ms-login-back" onClick={goBack} aria-label="Go back">
        <IonIcon icon={chevronBackOutline} />
      </RippleButton>
      <div className="ms-login-wrap">
        <div className="ms-login-card">
          <Link to="/app"><img src={MintSlipLogo} alt="MintSlip" className="ms-login-logo" /></Link>
          <h1 className="ms-login-title">Sign in</h1>
          <p className="ms-login-sub">Use the method you signed up with. One tap, no password.</p>

          {exchanging ? (
            <p className="ms-login-sub ms-login-exchanging"><span className="ms-spin ms-spin-dark" /> Signing you in…</p>
          ) : (
            <>
              {providers.apple && (
                <RippleButton type="button" className="ms-login-btn ms-login-apple" onClick={() => startOAuth("apple")}>
                  <IonIcon icon={logoApple} /> Continue with Apple
                </RippleButton>
              )}
              {providers.google && (
                <RippleButton type="button" className="ms-login-btn ms-login-google" onClick={() => startOAuth("google")}>
                  <IonIcon icon={logoGoogle} /> Continue with Google
                </RippleButton>
              )}
              {isNative && nativeGoogle && (
                <RippleButton type="button" className="ms-login-btn ms-login-google" onClick={nativeGoogleSignIn} disabled={googleBusy}>
                  <IonIcon icon={logoGoogle} /> {googleBusy ? "Signing in…" : "Continue with Google"}
                </RippleButton>
              )}

              {/* Everything below stays inside this same card: the "Use email
                  instead" toggle, the inline email form and the code entry all
                  render here, under the provider buttons. */}
              {!showEmail && !codeSent && (
                <button type="button" className="ms-login-usemail" onClick={() => setShowEmail(true)}>
                  Use email instead
                </button>
              )}

              {showEmail && !codeSent && (
                <form onSubmit={sendCode} className="ms-login-emailform">
                  {/* A new account is created on the first sign-in; the name
                      comes from the email and is editable in Manage profile */}
                  <input
                    className="ms-login-input"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <RippleButton type="submit" className="ms-login-btn" disabled={busy || !email.trim()}>
                    {busy ? (<><span className="ms-spin" /> Sending…</>) : "Send code"}
                  </RippleButton>
                </form>
              )}

              {codeSent && (
                <form onSubmit={verifyCode} className="ms-login-emailform">
                  <p className="ms-login-sub">We sent a 6-digit code to {email.trim()}. Enter it below.</p>
                  <IonInputOtp
                    className="ms-login-otp"
                    length={6}
                    type="number"
                    fill="outline"
                    autoFocus
                    value={code}
                    onIonInput={(e) => setCode((e.detail.value || "").replace(/\D/g, "").slice(0, 6))}
                  >
                    Didn't get it? Check spam, or use a different email below.
                  </IonInputOtp>
                  <RippleButton type="submit" className="ms-login-btn" disabled={verifying || code.length !== 6}>
                    {verifying ? (<><span className="ms-spin" /> Verifying…</>) : "Verify code"}
                  </RippleButton>
                  <button type="button" className="ms-login-secondary" onClick={() => { setCodeSent(false); setCode(""); }}>
                    Use a different email
                  </button>
                </form>
              )}
            </>
          )}

          <p className="ms-login-foot">New to MintSlip? Your account is created the first time you sign in.</p>
        </div>
      </div>
    </div>
  );
}
